import { Injectable } from "@angular/core";
import { FileLogger } from "@comunicare/comunicare-helper";
import {
  Appointment,
  IAppointment,
  ICommunication,
  IDataSourceBackendParam,
  IFilter,
  IPatientAppointement,
  ISchedule,
  ITeleconsultationInfo,
  TELECONSULTATION_PERIOD_KEY,
} from "@comunicare/comunicare-interfaces";
import { Observable } from "rxjs";
import { HealthcareserviceService } from "../healthcareservice.service";
import { SessionService } from "../session.service";
import { UserService } from "../user.service";
import { ApiService } from "./api.service";
import { BaseApiService } from "./baseApi.service";

@Injectable({
  providedIn: "root",
})
export class AppointmentApiService extends BaseApiService {
  constructor(
    private api: ApiService,
    private sessionService: SessionService,
    private healthcareService: HealthcareserviceService,
    private userService: UserService
  ) {
    super(
      "Appointments", // Theme !
      ["appointment", "dashboard/appointment/send"], // Post route paths
      [
        "appointmentsPractitioner",
        "patientsAppointments",
        "dashboard/teleconsultations",
        "dashboard/nbTeleconsultations",
        "dashboard/schedules",
      ], // Get route paths
      ["appointment"], // Update route paths
      [] // Delete route paths
    );
  }

  public list(): Observable<IAppointment[]> {
    const params = {
      language: this.sessionService.userLang,
    };
    return this.api.get(this.readRoutes[0], params) as Observable<IAppointment[]>;
  }

  public listVisioAppointements(patientId: string, allAppointments?: boolean): Observable<IAppointment[]> {
    const params = { patientId: patientId, allAppointments: allAppointments, language: this.sessionService.userLang };
    return this.api.get(this.readRoutes[0], params) as Observable<IAppointment[]>;
  }

  public getPatientAppointement(name: string): Observable<IPatientAppointement[]> {
    let currentServiceRef = this.sessionService.currentService;
    if (currentServiceRef.reference === this.sessionService.allsOption) {
      FileLogger.warn("AppointmentApiService", 'You are trying to get patients for appointment with service "all", result gonna be wonky');
      currentServiceRef = this.healthcareService.availableServices()[0].asReference;
    }
    const params = {
      name,
      serviceRef: currentServiceRef.reference,
      monitoringServicesIds: null,
    };
    if (this.userService.isMonitoringUser) {
      const monitServiceRef = this.sessionService.currentMonitoringService;
      const monitRefs: string[] = [];
      if (monitServiceRef.reference === this.sessionService.allsOption) {
        monitRefs.push(...this.healthcareService.availableMonitoringServices().map((s) => s.serviceRef));
      } else {
        monitRefs.push(monitServiceRef.reference);
      }
      params.monitoringServicesIds = monitRefs.join();
    }
    return this.api.get(this.readRoutes[1], params) as Observable<IPatientAppointement[]>;
  }

  public update(app: IAppointment): Observable<IAppointment> {
    return this.api.put(this.updateRoutes[0], app) as Observable<IAppointment>;
  }

  public createVisioAppointement(app: IAppointment): Observable<Appointment> {
    return this.api.post(this.createRoutes[0], app) as Observable<Appointment>;
  }

  public getTeleconsultations(
    params: IDataSourceBackendParam & { appointmentPeriod?: TELECONSULTATION_PERIOD_KEY }
  ): Observable<ITeleconsultationInfo[]> {
    let sortId = `teleconsultation.${params.sortId}`;

    if (sortId === "teleconsultation.start") {
      sortId = "teleconsultation.startDate";
    }

    const query = {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      search: params.search,
      sortId: sortId,
      services: JSON.stringify(params.services),
      filters: JSON.stringify(params.filters),
      organization: params.organization,
      sortOrder: params.sortOrder,
      appointmentPeriod: params.appointmentPeriod,
    };

    return this.api.get(this.readRoutes[2], query) as Observable<ITeleconsultationInfo[]>;
  }

  public getTeleconsultationsCount(
    filters?: IFilter[],
    services?: string[],
    org?: string,
    appointmentPeriod?: TELECONSULTATION_PERIOD_KEY
  ): Observable<{ count: number }> {
    return this.api.get(this.readRoutes[3], {
      filters: JSON.stringify(filters),
      services: JSON.stringify(services),
      organization: org,
      appointmentPeriod: appointmentPeriod,
    }) as Observable<{
      count: number;
    }>;
  }

  public getAgendaList(services: string[], org: string): Observable<ISchedule[]> {
    return this.api.get(this.readRoutes[4], {
      services: JSON.stringify(services),
      organization: org,
    }) as Observable<ISchedule[]>;
  }

  public resendMessageVisioAppointement(app: IAppointment): Observable<ICommunication[]> {
    return this.api.post(this.createRoutes[1], {}, { appointmentId: app._id }) as Observable<ICommunication[]>;
  }
}
